import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import './Settings.css';
import { API_BASE_URL } from '../../api';

const Settings = () => {
  const [settings, setSettings] = useState({
    profile: {
      shopName: '',
      ownerName: '',
      email: '',
      phone: '',
      shopAddress: '',
      logo_url: '',
      banner_url: '',
      statusLabel: ''
    }
  });

  const [isSaving, setIsSaving] = useState(false);

  const handleSettingChange = (category, key, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value
      }
    }));
  };

  const getVendorToken = () => {
    let token = localStorage.getItem('token');
    if (!token) {
      try {
        const v = JSON.parse(localStorage.getItem('vendorData') || 'null');
        token = v?.token || '';
      } catch (_) {}
    }
    return token;
  };

  const handleUpload = async (type, file) => {
    if (!file) return;
    try {
      const token = getVendorToken();
      const form = new FormData();
      if (type === 'logo') form.append('logo', file);
      if (type === 'banner') form.append('banner', file);
      const res = await fetch(`${API_BASE_URL}/api/vendor/profile/media`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Upload failed');
      } else {
        setSettings(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            logo_url: data.logo_url !== undefined ? data.logo_url : prev.profile.logo_url,
            banner_url: data.banner_url !== undefined ? data.banner_url : prev.profile.banner_url
          }
        }));
      }
    } catch (e) { console.error('Media upload error:', e); }
  };

  const removeMedia = async (type) => {
    try {
      const token = getVendorToken();
      const res = await fetch(`${API_BASE_URL}/api/vendor/profile/media?type=${encodeURIComponent(type)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Remove failed');
      } else {
        setSettings(prev => ({
          ...prev,
          profile: {
            ...prev.profile,
            logo_url: type === 'logo' ? '' : prev.profile.logo_url,
            banner_url: type === 'banner' ? '' : prev.profile.banner_url
          }
        }));
      }
    } catch (e) { console.error('Remove media error:', e); }
  };

  const loadVendorProfile = async () => {
    try {
      const token = getVendorToken();
      const res = await fetch(`${API_BASE_URL}/api/vendor/profile`, { headers: { 'Authorization': `Bearer ${token}` }});
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({
          ...prev,
          profile: {
            shopName: data.shopName || '',
            ownerName: data.ownerName || '',
            email: data.email || '',
            phone: data.phone || '',
            shopAddress: data.shopAddress || '',
            logo_url: data.logo_url || '',
            banner_url: data.banner_url || '',
            statusLabel: data.statusLabel || ''
          }
        }));
      }
    } catch (e) { console.error('Load profile error:', e); }
  };

  useEffect(() => { loadVendorProfile(); }, []);

  const handleSave = async () => {
    if (isSaving) return; // Prevent multiple saves
    
    setIsSaving(true);
    try {
      const token = getVendorToken();
      
      if (!token) {
        alert('Please log in to save changes');
        return;
      }
      
      // Save basic profile information
      const profileRes = await fetch(`${API_BASE_URL}/api/vendor/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopName: settings.profile.shopName,
          ownerName: settings.profile.ownerName,
          email: settings.profile.email,
          phone: settings.profile.phone,
          shopAddress: settings.profile.shopAddress
        })
      });
      
      if (!profileRes.ok) {
        const e = await profileRes.json();
        alert(e.error || 'Failed to save profile');
        return;
      }
      
      // Reload profile data to show updated information
      await loadVendorProfile();
      
      // Show success message
      alert('All changes saved successfully!');
      
    } catch (e) { 
      console.error('Save profile error:', e);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };


  const renderProfileSettings = () => (
    <div className="settings-section">
      <div className="shop-summary-card">
        <div className="shop-summary-content">
          {settings.profile.logo_url ? (
            <img src={settings.profile.logo_url} alt="logo" className="shop-logo" />
          ) : (
            <div className="shop-logo-placeholder">
              {settings.profile.shopName ? settings.profile.shopName.charAt(0).toUpperCase() : 'S'}
            </div>
          )}
          <div className="shop-summary-info">
            <h2 className="shop-name">{settings.profile.shopName || '—'}</h2>
            <div className="shop-details-row">
              <span className="shop-detail-item">{settings.profile.shopAddress || '—'}</span>
            </div>
            <div className="shop-details-row">
              <span className="shop-detail-item">{settings.profile.email || '—'}</span>
              {settings.profile.phone && <span className="shop-detail-separator">•</span>}
              {settings.profile.phone && <span className="shop-detail-item">{settings.profile.phone}</span>}
            </div>
          </div>
          {settings.profile.statusLabel && (
            <div className="shop-status-badge">
              {settings.profile.statusLabel}
            </div>
          )}
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="form-section">
        <h3 className="section-title">Shop & Owner Information</h3>
        <p className="section-description">Update your shop details and owner information</p>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Shop Name</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.shopName}
            onChange={(e) => handleSettingChange('profile', 'shopName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Owner Name</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.ownerName}
            onChange={(e) => handleSettingChange('profile', 'ownerName', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Email</label>
          <input
            type="email"
            className="form-input"
            value={settings.profile.email}
            onChange={(e) => handleSettingChange('profile', 'email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Phone Number</label>
          <input
            type="tel"
            className="form-input"
            value={settings.profile.phone}
            onChange={(e) => handleSettingChange('profile', 'phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label className="form-label">Shop Address</label>
          <input
            type="text"
            className="form-input"
            value={settings.profile.shopAddress}
            onChange={(e) => handleSettingChange('profile', 'shopAddress', e.target.value)}
          />
        </div>
      </div>

      </div>
    </div>
  );

  const renderContent = () => {
    // Always render profile settings for mobile-only view
    return renderProfileSettings();
  };

  return (
    <div className="settings">
      <div className="settings-header">
        <div className="header-content">
          <h1>Profile Settings</h1>
          <p>Manage your shop profile and details</p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={handleSave}
          disabled={isSaving}
        >
          <Save size={20} />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <div className="settings-container">
        {/* Sidebar removed for mobile-only single tab view */}
        
        <div className="settings-content">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Settings;
